/* ==================================================================
   Motor — Reflect On Engenharia
   ------------------------------------------------------------------
   Fonte ÚNICA da verdade do roteamento e do preço. Antes deste arquivo
   as regras viviam em duas cópias dentro do `legacy.js` (a calculadora
   da home e o motor do agente), e os números apareciam ainda no
   JSON-LD, no metadata e no prompt do assistente. Cinco lugares para
   uma verdade só é como um site passa a cobrar um valor e anunciar
   outro sem ninguém perceber.

   Agora é aqui, e o navegador recebe uma cópia GERADA deste código
   (`npm run motor` → `public/motor.js`), nunca uma cópia digitada.

   AS DUAS REGRAS QUE NÃO SE QUEBRAM:

     1. Na dúvida, nunca cobra.  (herdada do protótipo)
     2. O navegador nunca decide preço, quem decide é o servidor.

   A regra 2 é nova e é o motivo deste arquivo existir. Enquanto o
   destino do pedido era um link de WhatsApp, cálculo no cliente não
   custava nada. Com Stripe ligado, custa: qualquer pessoa abre o
   devtools, sobrescreve o total e compra a ART por R$ 1. Por isso
   `normalizarRespostas()` existe, e por isso `Respostas` não tem
   nenhum campo de preço — o que o cliente manda são RESPOSTAS, e a
   conta é sempre refeita deste lado.
   ================================================================== */

import { WHATSAPP } from "@/lib/contato";
import {
  LIMITE_FAIXA,
  LIMITE_PORTE,
  TABELA_VIGENTE,
  type Tabela,
  arredondar,
  brl,
  contaDaFaixa,
} from "./tabela";
import { ESCOPOS, type Escopo, type Respostas, type Resultado } from "./tipos";

export * from "./tipos";
export * from "./tabela";
export * from "./passos";
export * from "./descricao";

/* ------------------------------------------------------------------
   1. A fronteira: o que chega de fora não é confiável
   ------------------------------------------------------------------ */

/** Teto de sanidade pro valor declarado da obra. Não é regra de negócio:
 *  é anteparo contra `valorNum: 1e308` chegando pela rota e virando conta
 *  maluca ou `Infinity` no banco. Reforma de apartamento não passa disso. */
const TETO_VALOR_OBRA = 100_000_000;

function texto<T extends string>(v: unknown, permitidos: readonly T[]): T | undefined {
  return typeof v === "string" && (permitidos as readonly string[]).includes(v)
    ? (v as T)
    : undefined;
}

/**
 * Transforma JSON não confiável em `Respostas` válidas.
 *
 * É O PONTO DE ENTRADA OBRIGATÓRIO de tudo que vem do navegador. Chamar
 * `classificar()` direto com o corpo de uma requisição é o bug que este
 * arquivo inteiro existe pra impedir.
 *
 * Descarta em silêncio o que não reconhece, em vez de lançar erro: o
 * objetivo não é validar formulário (isso é da interface), é garantir
 * que o que segue adiante seja seguro. Campo de preço que venha junto é
 * ignorado por construção, porque nem sequer é lido.
 */
export function normalizarRespostas(bruto: unknown): Respostas {
  const b = (bruto ?? {}) as Record<string, unknown>;

  const escopo = Array.isArray(b.escopo)
    ? Array.from(
        new Set(
          b.escopo.filter((e): e is Escopo =>
            typeof e === "string" && (ESCOPOS as readonly string[]).includes(e),
          ),
        ),
      )
    : [];

  const r: Respostas = {
    escopo,
    estrutural: texto(b.estrutural, ["sim", "nao", "naosei"] as const),
    acompanha: texto(b.acompanha, ["sim", "nao"] as const),
    valor: texto(b.valor, ["ate15", "acima15"] as const),
  };

  const n = typeof b.valorNum === "number" ? b.valorNum : Number.NaN;
  if (Number.isFinite(n) && n > 0 && n <= TETO_VALOR_OBRA) {
    r.valorNum = arredondar(n);
  }

  /* `undefined` é quem ainda não opinou, e esse caso MANTÉM o memorial.
     Só um `false` explícito conta como "o cliente tirou". */
  if (typeof b.memorial === "boolean") r.memorial = b.memorial;

  return r;
}

/**
 * O valor declarado no cadastro manda na faixa, sempre.
 *
 * O cliente responde uma faixa por estimativa no começo da conversa e
 * depois digita o valor real no cadastro. Quando os dois discordam, vale
 * o número digitado — e a interface AVISA em voz alta que a conta mudou,
 * porque "preço fechado, sem surpresa" é a tese do site inteiro.
 */
export function normalizarFaixa(r: Respostas): Respostas {
  if (typeof r.valorNum !== "number") return r;
  return { ...r, valor: r.valorNum > LIMITE_FAIXA ? "acima15" : "ate15" };
}

/* ------------------------------------------------------------------
   2. A classificação
   ------------------------------------------------------------------ */

/** O padrão (`vemMarcado`) só vale enquanto a pessoa não opinou. Um `true`
 *  ou `false` explícito em `r.memorial` sempre vence, nos dois sentidos. */
function querMemorial(r: Respostas, vemMarcado: boolean): boolean {
  return typeof r.memorial === "boolean" ? r.memorial : vemMarcado;
}

const TEXTOS_ANALISE = {
  escopo:
    "Você marcou mudança em ponto de gás, que é o item de maior risco de uma reforma. Nesses " +
    "casos o Alan confere o escopo antes de emitir qualquer coisa, e antes de você pagar. " +
    "Ele responde em até 4 horas úteis com o valor fechado.",
  estrutural_incerto:
    "Como você não tem certeza se a parede é estrutural, o Alan vai conferir antes. É rápido, " +
    "e é o tipo de coisa que não dá pra chutar. Nada é cobrado até ele confirmar.",
  porte:
    "Obra desse porte costuma envolver mais coisas do que a lista mostra, então o Alan dá uma " +
    "olhada antes pra garantir que a ART cobre tudo que você vai fazer. " +
    "Ele responde em até 4 horas úteis com o valor fechado.",
} as const;

/**
 * Decide via, motivo e preço.
 *
 * @param respostas  o que o cliente respondeu — passe por `normalizarRespostas()` antes
 * @param tabela     a tabela vigente. No bloco 2 vem do banco; hoje é constante.
 */
export function classificar(
  respostas: Respostas,
  tabela: Tabela = TABELA_VIGENTE,
): Resultado {
  const r = normalizarFaixa(respostas);
  const escopo = r.escopo || [];

  /* ---------- as três portas de saída ----------
     Ordem importa: acompanhamento e parede estrutural recusam mesmo que o
     escopo pareça direto. */

  /* Pintura, gesso ou marcenaria SOZINHA cai aqui desde 14/09/2026. Antes
     ela seguia para preço e cobrava R$ 399, enquanto o próprio site promete
     por escrito o contrário (`lib/servicos.ts`, "quandoNaoPrecisa"), o texto
     desta recusa já citava pintura, e o assistente de texto já trata pintura
     como dispensa (`app/api/chat/route.ts`). Era o único caminho que vendia
     documento para uma obra que o site diz não precisar dele.

     Não perde a venda legítima: o botão da recusa leva ao WhatsApp do Alan,
     que é o mesmo destino que o FAQ dá para o síndico que exige ART de
     pintura. E quem marca pintura junto de piso, ar ou instalação continua
     no caminho de antes. */
  const soDecoracao =
    escopo.length > 0 && escopo.every((e) => e === "nenhum" || e === "pintura");

  if (soDecoracao) {
    return {
      via: "RECUSA",
      motivo: "sem_art",
      titulo: "Você provavelmente não precisa de ART",
      texto:
        "Reforma que é só decoração, pintura sem mexer em instalação ou troca de móvel não exige ART. " +
        "Na maioria dos condomínios basta avisar o síndico. Se a obra também mexe em fiação, ponto " +
        "de luz ou água, refaça marcando isso, porque aí o caso muda. Se o síndico insistir em pedir " +
        "o documento, chama a gente no WhatsApp que a gente conversa com ele.",
      cobra: false,
    };
  }

  if (r.acompanha === "sim") {
    return {
      via: "RECUSA",
      motivo: "acompanhamento",
      titulo: "O seu caso é outro serviço",
      texto:
        "Acompanhamento de obra é uma ART diferente, com visitas ao local durante a execução. " +
        "Não é o serviço que fazemos aqui, e não vamos te vender o documento errado. " +
        "Chama no WhatsApp que a gente indica o caminho certo.",
      cobra: false,
    };
  }

  if (r.estrutural === "sim") {
    return {
      via: "RECUSA",
      motivo: "estrutural",
      titulo: "Parede estrutural precisa de projeto, não de ART simples",
      texto:
        "Mexer em parede estrutural exige projeto de reforço com cálculo, e nenhuma ART de reforma " +
        "cobre isso. Emitir o documento assim colocaria você e o prédio em risco, então a gente " +
        "não emite. Chama no WhatsApp que a gente te explica o que o seu caso pede.",
      cobra: false,
    };
  }

  /* ---------- o que pede análise ----------
     Corrigido em 2026-08-20: valor alto NÃO implica complexidade técnica.
     Troca de piso num apartamento grande passa de R$ 15 mil e continua sendo
     troca de piso. O valor muda a faixa da taxa do CREA, não a natureza do
     serviço.

     Gás entra aqui e não na lista de casos diretos: instalação predial de gás
     é o item de maior risco que o site aceita, e o padrão da casa é o
     engenheiro olhar antes de vender. */
  const mexeEmInstalacao =
    escopo.indexOf("parede") > -1 ||
    escopo.indexOf("hidraulica") > -1 ||
    escopo.indexOf("eletrica") > -1 ||
    escopo.indexOf("gas") > -1;

  /* ---------- memorial: escolha do cliente, não do sistema ----------
     Corrigido em 2026-08-28, com o Alan. A versão anterior embutia o memorial
     sempre que a obra mexia em instalação, e dizia na tela que a NBR 16280
     obrigava. O engenheiro que assina desmentiu: na norma o memorial só é
     indispensável em intervenção estrutural e ampliação de área, que são
     exatamente os casos que este motor RECUSA logo acima. Ou seja, em 100% do
     que passa por aqui ele é OPCIONAL, e continua sendo — nada aqui reintroduz
     a alegação de obrigatoriedade.

     Ajustado em 2026-09-17: o que mudou é só o PADRÃO, não a regra. Antes
     vinha sempre marcado, para qualquer escopo. O primeiro pedido pago da
     campanha (17/09) marcou só "ar-condicionado" (não pede atenção), o
     memorial ficou junto por padrão, o total saiu de R$ 249 para R$ 399, e
     o pedido foi abandonado antes do cadastro. Onde o síndico raramente
     pede (escopo isolado e leve), o padrão agora é vazio; onde costuma
     pedir (mexe em instalação, ou mais de um item marcado), continua
     vindo marcado. O cliente sempre pode virar o padrão nos dois sentidos —
     "na dúvida, nunca cobra" agora também vale para o que vem marcado
     sozinho, não só para o que o motor recusa.

     Ajustado de novo em 2026-09-23, como TESTE da campanha: o memorial nasce
     desmarcado em TODO escopo. Das 7 pessoas que terminaram a triagem na
     primeira semana, as 5 que só viram R$ 399 ou R$ 600 pararam ali; das 2
     que viram R$ 249, uma chegou ao pagamento. O anúncio promete R$ 249.
     `alertaMemorial` continua decidindo o AVISO de que o síndico costuma
     pedir, que agora aparece com o memorial fora, oferecendo a inclusão.
     Para voltar ao padrão de 17/09: trocar o `false` abaixo por
     `alertaMemorial`, e o mesmo no `render()` do `public/legacy.js`. */
  const alertaMemorial =
    mexeEmInstalacao || escopo.filter((e) => e !== "nenhum").length > 1;
  const memorial = querMemorial(r, false);

  /* Cada motivo tem texto próprio. Mensagem genérica dizendo "você marcou
     intervenção em estrutura" pra quem marcou só piso é mentira, e mentira de
     sistema custa a confiança que o site inteiro tenta construir. */
  let motivo: keyof typeof TEXTOS_ANALISE | null = null;
  /* Decidido pelo Alan em 2026-09-23, depois da primeira semana da campanha
     (4 de 7 pessoas pararam na tela de análise, nenhuma seguiu): SÓ GÁS vai
     para a análise dele. Parede não estrutural, hidráulica e elétrica passam
     a ser pagas direto. `mexeEmInstalacao` continua valendo para o aviso do
     memorial, que é outra pergunta. A parede estrutural segue recusada, e a
     parede de que a pessoa não tem certeza segue em análise, logo abaixo. */
  const pedeAnalise = escopo.indexOf("gas") > -1;
  if (pedeAnalise) motivo = "escopo";
  else if (r.estrutural === "naosei") motivo = "estrutural_incerto";
  else if ((r.valorNum || 0) >= LIMITE_PORTE) motivo = "porte";

  const acima = r.valor === "acima15";
  const conta = contaDaFaixa(acima, memorial, tabela);

  const comum = {
    servico: conta.servico,
    taxa: conta.taxa,
    memorial,
    alertaMemorial,
    total: conta.total,
    tabelaCreaId: conta.crea.id,
  };

  if (motivo) {
    return {
      ...comum,
      via: "ANALISE",
      motivo,
      titulo:
        motivo === "porte"
          ? "Vale o engenheiro dar uma olhada antes"
          : "O engenheiro precisa olhar o seu caso antes",
      texto: TEXTOS_ANALISE[motivo],
      cobra: false,
    };
  }

  return {
    ...comum,
    via: "LIMPO",
    motivo: null,
    titulo: "Seu caso é direto, dá pra resolver agora",
    texto:
      "Pelo que você descreveu, a ART sozinha resolve e não há nada que precise de análise " +
      "prévia. O engenheiro confere o escopo e emite em até 4 horas úteis.",
    cobra: true,
  };
}

/** Atalho do servidor: normaliza e classifica num passo só, que é como as
 *  rotas devem usar. Existe pra que ninguém pule o `normalizarRespostas()`
 *  por descuido. */
export function classificarEntrada(bruto: unknown, tabela?: Tabela): Resultado {
  return classificar(normalizarRespostas(bruto), tabela);
}

export { WHATSAPP, brl };
